import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { asUserId } from '@leanin/shared/ids';
import {
  CommentsPanel,
  type CommentItemView,
  type ReplyThreadData,
} from '../comments/index';
import {
  useFeedComment,
  useFeedCommentReplies,
  useFeedComments,
} from '../../lib/queries/feed';
import {
  useCreateFeedComment,
  useResolveLinkPreview,
  type LinkPreviewInput,
} from '../../lib/mutations/feed';
import { EDITOR_BODY_FORMAT } from '../editor';
import { identityKeys } from '../../lib/queries/identity';
import { useViewerCanMentionAll } from '../../lib/queries/groups';
import { usePostCommentHandlers } from './use-post-comment-handlers';
import { mentionDisplayName } from './MentionPopover';
import { commentViewToItem } from './feed-item-to-post';
import type { ContentSection } from '../../lib/analytics/events';
import type { PostRemovalTarget } from './post-removal-targets';
import type { FeedPostAuthor } from './types';

// `identityKeys.member` needs a concrete id; the `enabled` gate keeps the
// query dormant for a deleted author, so this placeholder is never fetched.
const NO_PROFILE_ID = '';

// Post flavor of the shared CommentsPanel — feed queries/mutations +
// feed error copy. The panel owns all rendering and interaction state.

type CommentView = components['schemas']['CommentView'];

// Passed to the panel's `replies.useThread` — called by the panel's
// always-mounted per-thread component, so the hook order is stable.
function useReplyThreadForPost(
  parentCommentId: string,
  opts: { enabled: boolean },
): ReplyThreadData {
  const query = useFeedCommentReplies(parentCommentId, opts);
  return {
    items: (query.data?.pages ?? []).flatMap((p) => p.items).map(commentViewToItem),
    hasMore: query.hasNextPage === true,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    fetchMore: () => void query.fetchNextPage(),
    refetch: () => void query.refetch(),
  };
}

// Fallback card for a pasted link that can't be unfurled into a rich preview
// (unreachable, non-HTML, or a 403 bot-wall). Same degradation the post
// composer applies — a pasted link always resolves to a card.
function minimalLinkPreview(url: string): LinkPreviewInput {
  return {
    url,
    title: null,
    description: null,
    imageStorageKey: null,
    siteName: null,
  };
}

type Props = {
  postId: string;
  /** The post's author. A member's profile is resolved (shared cache with
   *  reply-to-reply mention prefill) and pinned to the top of comment
   *  @-mention suggestions — the person most likely tagged in a reply or
   *  comment. A deleted author has no profile and is never suggested. */
  postAuthor: FeedPostAuthor;
  /** The post's analytics content_section (computed by the card that renders
   *  the post — PostComments never sees the post itself). */
  section: ContentSection;
  /** The post's audience group (its single circle/network/space scope), so
   *  comment @-mentions only suggest people who can see the post. Undefined
   *  for global posts → suggest anyone. */
  audienceGroupId?: string;
  /** The audience group's name, when the post has a single scope. With the
   *  viewer's role in the group it decides whether the comment composers
   *  offer the whole-group "@all" row (direct leaders only). */
  audienceGroupName?: string;
  /** The post's `viewerCan.comment` — off for viewers who can read the thread
   *  but may not add to it. Gates the composer and the Reply affordance. */
  canComment?: boolean;
  /** The post's `viewerCan.react` — comment reactions inherit the parent
   *  post's gate (`commentRules.reactable` composes `postRules.reactable`).
   *  Separate from `canComment` because they are two server answers, not one
   *  local notion of "interactive". */
  canReact?: boolean;
  /** Deep-link target (`/posts/{id}?comment=…`) — scrolled into view and
   *  flashed once rendered, its reply thread auto-expanded when needed. */
  highlightCommentId?: string;
  /** Pin the composer to the bottom of the scrolling post dialog. */
  pinComposer?: boolean;
  /** Open with the reply composer under this top-level comment. */
  initialReplyCommentId?: string;
  /** Told whether the thread holds text the viewer hasn't posted or saved. */
  onUnsentChange?: (unsent: boolean) => void;
  /** The group a leader removes comments from; omit when the viewer leads
   *  none of the post's groups. */
  removalGroup?: PostRemovalTarget;
};

export const PostComments = ({
  postId,
  postAuthor,
  section,
  audienceGroupId,
  audienceGroupName,
  canComment = true,
  canReact = true,
  highlightCommentId,
  pinComposer = false,
  initialReplyCommentId,
  onUnsentChange,
  removalGroup,
}: Props) => {
  const qc = useQueryClient();
  // Resolve the post author's profile so comment @-mention suggestions can pin
  // them first. Only when the viewer can actually compose; shares the
  // react-query cache with the reply-to-reply `mentionForAuthor` prefill.
  const authorProfile = useQuery({
    ...identityKeys.member(
      asUserId(postAuthor.kind === 'member' ? postAuthor.id : NO_PROFILE_ID),
    ),
    enabled: canComment && postAuthor.kind === 'member',
  });
  const commentsQuery = useFeedComments(postId);
  const createComment = useCreateFeedComment();
  const handlers = usePostCommentHandlers({ postId, canReact, removalGroup });
  const resolveLinkPreview = useResolveLinkPreview();

  // A deep-linked comment may be a reply — resolve its parent so the panel
  // knows which thread to auto-expand before scrolling. The empty-string id
  // never fetches: the enabled gate keeps the query dormant.
  const highlightQuery = useFeedComment(highlightCommentId ?? '', {
    enabled: highlightCommentId !== undefined,
  });
  // Whether a comment may address the whole audience group: the server's own
  // `@all` rule for it (direct leader or platform admin), so the affordance
  // matches what the write will notify. Skipped for global posts (no single
  // group).
  const audienceCanMentionAll = useViewerCanMentionAll(audienceGroupId ?? '', {
    enabled: canComment && audienceGroupId !== undefined,
  });
  const mentionAllGroup =
    canComment && audienceGroupName !== undefined && audienceCanMentionAll.data === true
      ? { name: audienceGroupName }
      : undefined;

  const highlight =
    highlightCommentId !== undefined && highlightQuery.data !== undefined
      ? {
          commentId: highlightCommentId,
          parentCommentId: highlightQuery.data.parentCommentId,
        }
      : undefined;

  // The server returns top-level comments newest-first (keyset DESC) and the
  // "next page" walks toward older comments; replies live in their own
  // per-thread query. A conversation reads oldest→newest top to bottom, so
  // reverse the accumulated window for display: a freshly posted comment
  // lands at the bottom beside the composer, and "View earlier comments"
  // prepends older history above. flatMap allocates a fresh array, so
  // reversing it never mutates the cached pages.
  const items = useMemo(() => {
    const pages = commentsQuery.data?.pages ?? [];
    return pages.flatMap((p) => p.items).reverse().map(commentViewToItem);
  }, [commentsQuery.data]);

  // Each page carries the oldest visible reply of its top-level comments —
  // zipped by parentCommentId so ReplyThread renders seeded without a
  // per-thread fetch. Pages cached before this field shipped may lack it.
  const firstReplies = useMemo(() => {
    const map = new Map<string, CommentItemView>();
    const pages = commentsQuery.data?.pages ?? [];
    for (const page of pages) {
      const seeded: CommentView[] | undefined = page.firstReplies;
      for (const reply of seeded ?? []) {
        if (reply.parentCommentId != null) {
          map.set(reply.parentCommentId, commentViewToItem(reply));
        }
      }
    }
    return map;
  }, [commentsQuery.data]);

  return (
    <CommentsPanel
      targetId={postId}
      pinComposer={pinComposer}
      {...(onUnsentChange !== undefined ? { onUnsentChange } : {})}
      {...(initialReplyCommentId !== undefined
        ? { initialReplyThreadId: initialReplyCommentId }
        : {})}
      items={items}
      isLoading={commentsQuery.isPending}
      loadError={commentsQuery.isError ? commentsQuery.error : null}
      hasEarlier={commentsQuery.hasNextPage === true}
      isFetchingEarlier={commentsQuery.isFetchingNextPage}
      onFetchEarlier={() => void commentsQuery.fetchNextPage()}
      {...handlers}
      onCreate={async (body, linkPreviews) => {
        // Stable idempotency key for this submit — a timed-out-but-committed
        // request that the user retries collapses onto the same comment row
        // server-side instead of duplicating.
        await createComment.mutateAsync({
          postId,
          body,
          bodyFormat: EDITOR_BODY_FORMAT,
          linkPreviews: [...linkPreviews],
          clientCommentId: crypto.randomUUID(),
          section,
        });
      }}
      resolveLinkPreview={async (url) => {
        try {
          const unfurled = await resolveLinkPreview.mutateAsync({ url });
          return unfurled.preview ?? minimalLinkPreview(url);
        } catch {
          return minimalLinkPreview(url);
        }
      }}
      {...(audienceGroupId !== undefined ? { audienceGroupId } : {})}
      {...(mentionAllGroup !== undefined ? { mentionAllGroup } : {})}
      {...(authorProfile.data !== undefined
        ? { mentionPriorityMember: authorProfile.data }
        : {})}
      composerEnabled={canComment}
      replies={{
        useThread: useReplyThreadForPost,
        firstReplies,
        onCreateReply: async (parentCommentId, body) => {
          await createComment.mutateAsync({
            postId,
            body,
            bodyFormat: EDITOR_BODY_FORMAT,
            parentCommentId,
            clientCommentId: crypto.randomUUID(),
            section,
          });
        },
        // Reply-to-reply prefill: the comment payload has no author handle,
        // so pull the (react-query-cached) profile for it.
        mentionForAuthor: async (comment) => {
          if (comment.author.kind === 'deleted') return null;
          const profile = await qc.fetchQuery(
            identityKeys.member(asUserId(comment.author.id)),
          );
          return {
            kind: 'member',
            handle: profile.handle,
            userId: comment.author.id,
            name: mentionDisplayName(profile),
          };
        },
      }}
      {...(highlight !== undefined ? { highlight } : {})}
    />
  );
};
